import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Card from '../../components/Card/Card';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import api from '../../services/api';
import { EventWithUUID, FrontendInvite, RSVPGuest } from '../../types';
import { formatDateRange, getErrorMessage } from '../../utils/helpers';
import './RSVP.scss';

const RSVP: React.FC = () => {
  const { eventCode: eventCodeParam } = useParams<{ eventCode?: string }>();

  const hostname = window.location.hostname;
  const [eventCode, setEventCode] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [event, setEvent] = useState<EventWithUUID | null>(null);
  const [invite, setInvite] = useState<FrontendInvite | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [email, setEmail] = useState('');
  const [dietary, setDietary] = useState('');
  const [songRequest, setSongRequest] = useState('');
  const [otherInfo, setOtherInfo] = useState('');
  const [guestRsvps, setGuestRsvps] = useState<Record<string, boolean>>({});
  const [hasResponded, setHasResponded] = useState(false);
  
  // Multi-stage form state
  const [formStage, setFormStage] = useState<1 | 2 | 3>(1); // 1: Guest Attendance, 2: Contact, 3: Additional Info

  // Load Adobe Fonts only while on the RSVP page
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://use.typekit.net/jbu1zjh.css';
    if (hostname === 'www.alexlibbywedding.co.uk') {
      link.href = 'https://use.typekit.net/ydl0lfp.css';
    }
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, [hostname]);

  // Set event code from URL parameter and fetch event details
  useEffect(() => {
    if (eventCodeParam) {
      const upperCode = eventCodeParam.toUpperCase();
      setEventCode(upperCode);
      fetchEventDetails(upperCode);
    }
  }, [eventCodeParam]);

  const fetchEventDetails = async (code: string) => {
    setLoadingEvent(true);
    setError('');
    try {
      const eventData = await api.getEventByShortcode(code);
      setEvent(eventData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInvite(null);
    setLoading(true);

    try {
      const data = await api.getInviteByShortcode(
        eventCode.toUpperCase(),
        inviteCode.toUpperCase()
      );
      setInvite(data);
      
      // Pre-populate form if invite has data
      setEmail(data.invite.email || '');
      setDietary(data.invite.dietary || '');
      setSongRequest(data.invite.songRequest || '');
      setOtherInfo(data.invite.otherInfo || '');
      
      // Initialize guest RSVP states
      const rsvps: Record<string, boolean> = {};
      let alreadyResponded = false;
      data.guests.forEach(guest => {
        if (guest.rsvp !== null && guest.rsvp !== undefined) {
          rsvps[guest.uuid] = guest.rsvp;
          alreadyResponded = true;
        }
      });
      setGuestRsvps(rsvps);
      setHasResponded(alreadyResponded);
      setFormStage(1); // Reset to stage 1 when loading new invite
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGuestRsvpToggle = (guestUuid: string, attending: boolean) => {
    setGuestRsvps(prev => ({
      ...prev,
      [guestUuid]: attending,
    }));
  };

  const canProceedFromGuestAttendance = () => {
    if (!invite) return false;
    // All guests must have an RSVP status selected
    return invite.guests.every(guest => guestRsvps[guest.uuid] !== undefined);
  };

  const handleNextFromGuestAttendance = () => {
    if (!canProceedFromGuestAttendance()) {
      setError('Please select attendance status for all guests.');
      return;
    }
    setError('');
    setFormStage(2);
  };

  const handleNextFromContact = () => {
    // Validate email if any guest is attending
    const anyAttending = Object.values(guestRsvps).some(rsvp => rsvp === true);
    if (anyAttending && !email.trim()) {
      setError('A contact email is required when at least one guest is attending.');
      return;
    }
    setError('');
    setFormStage(3);
  };

  const handleBackToGuestAttendance = () => {
    setError('');
    setFormStage(1);
  };

  const handleBackToContact = () => {
    setError('');
    setFormStage(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;

    // Final validation (should already be validated by stage 2)
    const anyAttending = Object.values(guestRsvps).some(rsvp => rsvp === true);
    if (anyAttending && !email.trim()) {
      setError('A contact email is required when at least one guest is attending.');
      return;
    }

    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const rsvpData = {
        email: email.trim() || undefined,
        dietary: dietary.trim() || undefined,
        songRequest: songRequest.trim() || undefined,
        otherInfo: otherInfo.trim() || undefined,
        guests: Object.entries(guestRsvps).map(([uuid, rsvp]) => ({
          uuid,
          rsvp,
        })) as RSVPGuest[],
      };

      await api.rsvpInvite(invite.event.uuid, invite.invite.uuid, rsvpData);
      
      setSuccess('Thank you! Your RSVP has been submitted successfully.');
      setHasResponded(true);
      
      // Update local state
      setInvite({
        ...invite,
        invite: {
          ...invite.invite,
          email: email.trim() || invite.invite.email,
          dietary: dietary.trim() || invite.invite.dietary,
          songRequest: songRequest.trim() || invite.invite.songRequest,
          otherInfo: otherInfo.trim() || invite.invite.otherInfo,
        },
        guests: invite.guests.map(guest => ({
          ...guest,
          rsvp: guestRsvps[guest.uuid] ?? guest.rsvp,
        })),
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const theme = hostname === 'www.alexlibbywedding.co.uk' ? 'alexlibby' : 'griffkinson';

  return (
    <div className="rsvp-page" data-theme={theme}>
      <div className="container">
        {loadingEvent ? (
          <Loading message="Loading event details..." />
        ) : (
          <>
            <div className="rsvp-page__header">
              <h1>{event ? `RSVP to ${event.title}` : invite ? `RSVP to ${invite.event.title}` : 'RSVP to Event'}</h1>
              {event && (
                <>
                <p className="event-date">{formatDateRange(event.startDate, event.endDate)}</p>
                {event.description && (
                  <p className="event-description">{event.description}</p>
                )}
                </>)}
            </div>

            {!invite ? (
              <>
                <Card className="rsvp-lookup-card">
                  <div className="instructions-text">
                    <p className="text-muted">
                      {event
                        ? 'Enter your invite code to respond to your invitation'
                        : eventCodeParam 
                          ? 'Enter your invite code to respond to your invitation'
                          : 'Enter your event and invite codes to respond to your invitation'
                      }
                    </p>
                  </div>
                  <form onSubmit={handleLookup}>
                    <div className="code-inputs">
                      {!eventCodeParam && (
                        <Input
                          label="Event Code"
                          type="text"
                          name="eventCode"
                          value={eventCode}
                          onChange={(e) => setEventCode(e.target.value.toUpperCase())}
                          required
                          placeholder="e.g., HDTC22"
                        />
                      )}

                      <Input
                        label="Invite Code"
                        type="text"
                        name="inviteCode"
                        value={inviteCode}
                        onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                        required
                        placeholder="e.g., BC2H"
                      />
                    </div>

                    <Button type="submit" fullWidth disabled={loading}>
                      {loading ? 'Looking up...' : 'Find My Invitation'}
                    </Button>
                  </form>
                </Card>
              </>
            ) : (
              <Card className="rsvp-details-card">
                {success && (
                  <div className="success-message-inline">
                    <p>{success}</p>
                  </div>
                )}

                {hasResponded ? (
                  <div className="rsvp-submitted-section">
                    <h3>RSVP Already Submitted</h3>
                    <p className="submitted-message">
                      You have already responded to this invitation. If you need to change 
                      your response, please contact the event organizer.
                    </p>
                    
                    <div className="current-responses">
                      <h4>Current Responses:</h4>
                      <div className="guests-status-list">
                        {invite.guests.map(guest => (
                          <div key={guest.uuid} className="guest-status-item">
                            <span className="guest-status-name">{guest.name}</span>
                            {guest.rsvp === true && <span className="status-attending">✓ Attending</span>}
                            {guest.rsvp === false && <span className="status-not-attending">✗ Not Attending</span>}
                            {(guest.rsvp === null || guest.rsvp === undefined) && <span className="status-pending">? No Response</span>}
                          </div>
                        ))}
                      </div>
                      
                      {invite.invite.email && (
                        <p className="submitted-detail">
                          <strong>Email:</strong> {invite.invite.email}
                        </p>
                      )}
                      {invite.invite.dietary && (
                        <p className="submitted-detail">
                          <strong>Dietary Requirements:</strong> {invite.invite.dietary}
                        </p>
                      )}
                      {invite.invite.songRequest && (
                        <p className="submitted-detail">
                          <strong>Song Request:</strong> {invite.invite.songRequest}
                        </p>
                      )}
                      {invite.invite.otherInfo && (
                        <p className="submitted-detail">
                          <strong>Other Information:</strong> {invite.invite.otherInfo}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="rsvp-form">
                    {/* Stage 1: Guest Attendance */}
                    {formStage === 1 && (
                      <div className="guests-section">
                        <h3>Guest Attendance</h3>
                        <p className="section-description">Please indicate who will be attending:</p>
                        
                        <div className="guests-rsvp-list">
                          {invite.guests.map(guest => (
                            <div key={guest.uuid} className="guest-rsvp-item">
                              <div className="guest-rsvp-name">{guest.name}</div>
                              <div className="guest-rsvp-buttons">
                                <Button
                                  type="button"
                                  variant={guestRsvps[guest.uuid] === true ? 'secondary' : 'outline'}
                                  size="small"
                                  onClick={() => handleGuestRsvpToggle(guest.uuid, true)}
                                >
                                  ✓ Attending
                                </Button>
                                <Button
                                  type="button"
                                  variant={guestRsvps[guest.uuid] === false ? 'danger' : 'outline'}
                                  size="small"
                                  onClick={() => handleGuestRsvpToggle(guest.uuid, false)}
                                >
                                  ✗ Not Attending
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <Button 
                          type="button"
                          fullWidth 
                          size="large" 
                          onClick={handleNextFromGuestAttendance}
                          disabled={!canProceedFromGuestAttendance()}
                        >
                          Next
                        </Button>
                      </div>
                    )}

                    {/* Stage 2: Contact Information */}
                    {formStage === 2 && (
                      <div className="contact-section">
                        <h3>Contact Information</h3>
                        <p className="section-description">
                          {Object.values(guestRsvps).some(rsvp => rsvp === true)
                            ? 'A contact email is required for attending guests.'
                            : 'You can optionally provide contact information.'} We will use this to send you any updates about the event.
                        </p>
                        
                        <Input
                          label={Object.values(guestRsvps).some(rsvp => rsvp === true) ? "Email Address" : "Email Address (Optional)"}
                          type="email"
                          name="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required={Object.values(guestRsvps).some(rsvp => rsvp === true)}
                          placeholder="your.email@example.com"
                        />

                        <div className="form-navigation">
                          <Button 
                            type="button"
                            variant="outline"
                            onClick={handleBackToGuestAttendance}
                          >
                            Back
                          </Button>
                          <Button 
                            type="button"
                            onClick={handleNextFromContact}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Stage 3: Additional Information */}
                    {formStage === 3 && (
                      <div className="additional-info-section">
                        <h3>Additional Information</h3>
                        <p className="section-description">
                          {Object.values(guestRsvps).some(rsvp => rsvp === true)
                            ? 'Please let us know about any dietary requirements or other information you think we should know. We\'d also love to hear any song requests you have for the playlist!'
                            : 'Feel free to provide any additional information you think we should know.'}
                        </p>
                        
                        {Object.values(guestRsvps).some(rsvp => rsvp === true) && (
                          <>
                            <Input
                              label="Dietary Requirements (Optional)"
                              type="text"
                              name="dietary"
                              value={dietary}
                              onChange={(e) => setDietary(e.target.value)}
                              placeholder="e.g., Vegetarian, Gluten-free, Nut Allergy etc. Please leave blank if no special requirements are needed."
                            />

                            <Input
                              label="Song Request (Optional)"
                              type="text"
                              name="songRequest"
                              value={songRequest}
                              onChange={(e) => setSongRequest(e.target.value)}
                              placeholder="e.g., Dancing Queen by ABBA. Please leave blank if you have no song requests."
                            />
                          </>
                        )}
                        
                        <Input
                          label="Other Information (Optional)"
                          name="otherInfo"
                          value={otherInfo}
                          onChange={(e) => setOtherInfo(e.target.value)}
                          placeholder="Any other information you'd like us to know. Please leave blank if not needed."
                          multiline
                          rows={3}
                        />

                        <div className="form-navigation">
                          <Button 
                            type="button"
                            variant="outline"
                            onClick={handleBackToContact}
                          >
                            Back
                          </Button>
                          <Button 
                            type="submit"
                            disabled={submitting}
                          >
                            {submitting ? 'Submitting...' : 'Submit RSVP'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </form>
                )}
              </Card>
            )}
          </>
        )}

        {error && !loadingEvent && (
          <Card className="message-card error-card">
            <p>{error}</p>
          </Card>
        )}

        {success && !invite && (
          <Card className="message-card success-card">
            <p>{success}</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RSVP;
